import { create } from 'zustand';
import {
  queryHistory,
  queryHistoryUiData,
  deleteAllHistory as deleteAllHistoryApi,
  reportHistory,
  syncTime,
  getHistoryCidCover,
} from '@/services';
import { HistoryRecord, Poster, OpType, IDType, VideoType } from '@/types/common';
import {
  HistoryFetchAllHistoryParams,
  HistoryApiResponse,
  HistoryStoreState,
  HistoryStoreStore,
  HistorySaveItem,
  HistoryShowItem,
} from '@/types/history';
import {
  getLocalHistory,
  saveHistoryToStorage,
  updateSingleHistory,
  updateSingleHistoryForCurShow,
  deleteAllHistoryRecords,
  queryHistoryByCid,
  handleHistoryData,
  getIsSameUser,
  handleCoverAndUiData,
  processBatchData,
} from '@/utils/historyHandler';
import { logger } from '@/utils/devUtils';
import { usePlayDetailStore } from '@/pages/playDetail/store';

const batchSize = 30;

const createInitialState = (overrides?: Partial<HistoryStoreState>): HistoryStoreState => ({
  vuid: '',
  curHistory: [],
  curShowHistory: [],
  isLoading: false,
  isLoaded: false,
  ...overrides,
});

export const useHistoryStore = create<HistoryStoreStore>((set, get) => {
  const initialState = createInitialState();
  return {
    ...initialState,
    // 设置 vuid - 处理用户切换场景
    setVuid: (vuid: string) => {
      const { vuid: currentVuid, initHistory } = get();
      // 标准化 vuid（空字符串、null、undefined 都视为未登录状态）
      const newVuid = vuid || '';
      const curVuid = currentVuid || '';
      // 判断 vuid 是否发生变化，vuid 一致，不处理
      if (newVuid && curVuid && newVuid === curVuid) return;
      // 设置新的vuid，并标记为加载中
      set(
        createInitialState({
          vuid: newVuid,
          isLoading: true,
          isLoaded: false,
        })
      );
      // vuid 不一致 并且 属于切换账号，清除登录历史记录（localStorage）
      // 这样可以防止用户切换账号后，下次进来读取的还是上一个用户的 dataVersion
      if (newVuid && curVuid) deleteAllHistoryRecords(true);
      // 根据新的 vuid 重新初始化历史记录
      initHistory();
    },

    // 同步本地历史记录（如果本地记录时间戳领先，则分页上报）
    // 根据流程图：对比本地记录与后台记录，如果本地记录时间戳领先，则分页上报（每30条）
    syncLocalHistoryIfNeeded: async (localHistory, serverHistory) => {
      try {
        const { vuid } = get();
        const isLogin = !!vuid;

        // 只有登录状态才需要同步
        if (!isLogin) return;

        // 构建服务器历史记录的Map，用于快速查找
        const serverHistoryMap = new Map<string, HistorySaveItem>();
        serverHistory.forEach((item) => {
          const key = `${item.cid}_${item.vid}_${item.type || ''}`;
          serverHistoryMap.set(key, item);
        });

        // 找出本地记录时间戳领先的记录
        const localAheadRecords: HistorySaveItem[] = [];
        localHistory.forEach((localItem) => {
          const key = `${localItem.cid}_${localItem.vid}_${localItem.type || ''}`;
          const serverItem = serverHistoryMap.get(key);

          // 如果服务器没有该记录，或者本地记录时间戳更新，则需要上报
          if (!serverItem) {
            localAheadRecords.push(localItem);
          } else {
            const localTime = parseInt(localItem.modifyTime, 10);
            const serverTime = parseInt(serverItem.modifyTime, 10);
            if (localTime > serverTime) {
              localAheadRecords.push(localItem);
            }
          }
        });

        // 如果没有需要上报的记录，直接返回
        if (localAheadRecords.length === 0) {
          return;
        }

        console.log('[HistoryStore] 发现本地记录时间戳领先，需要上报', localAheadRecords.length, '条记录');

        // 分页上报，每30条一批
        const PAGE_SIZE = 30;
        for (let i = 0; i < localAheadRecords.length; i += PAGE_SIZE) {
          const pageRecords = localAheadRecords.slice(i, i + PAGE_SIZE);

          // 构建上报数据
          const reportData = {
            dataVersion: `${Date.now()}`,
            updated: pageRecords.map((item) => ({
              record: {
                vid: item.vid,
                cid: item.cid,
                modify_time: item.modifyTime,
                video_play_time: item.videoPlayTime,
                video_total_time: item.videoTotalTime,
              },
              operation_type: OpType.OpTypeUpdate,
              record_type: IDType.RecordTypeCid,
            })),
          };

          // 调用上报接口
          try {
            await reportHistory(reportData, {});
            console.log(
              `[HistoryStore] 已上报第 ${Math.floor(i / PAGE_SIZE) + 1} 页历史记录`,
              pageRecords.length,
              '条'
            );
          } catch (error) {
            console.error('[HistoryStore] 上报历史记录失败:', error);
            // 上报失败不影响主流程，继续上报下一页
          }
        }
      } catch (error) {
        console.error('[HistoryStore] 同步本地历史记录失败:', error);
      }
    },

    // 重置历史记录到初始状态
    reset: () => {
      const { vuid: currentVuid } = get();
      // 重置为初始状态，但保留当前 vuid
      set(createInitialState({ vuid: currentVuid }));
      console.log('History store reset to initial state');
    },

    initHistory: async () => {
      try {
        set({ isLoading: true, isLoaded: false, curShowHistory: [] });
        const { vuid, fetchHistory, fetchHistoryUiData, fetchHistoryCoverData } = get();
        const isLogin = !!vuid;
        const localHistory = getLocalHistory(vuid);
        const { dataVersion = '', history = [] } = localHistory || {};
        if (isLogin) {
          try {
            await fetchHistory({
              dataVersion: dataVersion || '0',
              historyInfo: history,
            });
          } catch (fetchError) {
            console.error('Failed to fetch server history:', fetchError);
            // 如果获取服务器历史记录失败，使用本地历史记录
            set({
              curHistory: history,
            });
          }
        } else {
          set({
            curHistory: history,
          });
        }
        // 获取最新的 allHistory，避免使用旧的引用
        const { curHistory } = get();
        // 只有当有数据时才调用 fetchHistoryUiData，避免不必要的请求
        if (curHistory.length > 0) {
          try {
            const [uiData, coverData] = await Promise.all([
              fetchHistoryUiData(curHistory),
              fetchHistoryCoverData(curHistory),
            ]);
            const curShowHistoryItem = handleCoverAndUiData(uiData, coverData);
            const { vuid: curVuid } = get();
            if (curVuid === vuid) {
              set({
                curShowHistory: curShowHistoryItem,
                isLoaded: true,
                isLoading: false,
              });
            }
          } catch (uiError) {
            console.error('Failed to fetch history UI/cover data:', uiError);
            // 即使 UI 数据获取失败，也完成初始化
            set({ isLoaded: true, isLoading: false });
          }
        } else {
          set({ isLoaded: true, isLoading: false });
        }
      } catch (error) {
        set({ isLoaded: true, isLoading: false });
        console.error('Failed to init history from localStorage:', error);
      }
    },

    // 轮询获取所有历史记录
    fetchAllHistory: async (fetchAllHistoryParams: HistoryFetchAllHistoryParams) => {
      const { historyInfo, startVuid, dataVersion, pageContext, hasNextPage } = fetchAllHistoryParams;
      // 前置校验
      const { vuid } = get();
      // 判断是否是同一个用户，不是同一个用户，报错，不处理
      const isSanmeUser = getIsSameUser(vuid, startVuid);
      if (!isSanmeUser) throw new Error('User has changed');
      if (!hasNextPage)
        return {
          serverHistory: historyInfo,
          dataVersion,
        };

      // 是同一个用户，继续处理
      try {
        const requestBody = { data_version: dataVersion, page_context: pageContext };
        // 请求结果
        const data = (await queryHistory(requestBody)) as HistoryApiResponse;
        const {
          data_version: dataVersionNext,
          page_context: pageContextNext,
          has_next_page: hasNextPageNext,
          updated = [],
        } = data;

        // 请求结果无问题 处理响应数据
        const mergedHistory = [...historyInfo, ...(updated || [])];
        // 递归获取下一页
        if (!!hasNextPageNext) {
          return await get().fetchAllHistory({
            historyInfo: mergedHistory,
            startVuid,
            dataVersion: dataVersionNext,
            pageContext: pageContextNext,
            hasNextPage: hasNextPageNext,
          });
        }
        // 没有下一页，返回结果
        return {
          serverHistory: mergedHistory,
          dataVersion: dataVersionNext,
        };
      } catch {
        // 请求，返回已获取的历史记录
        return {
          serverHistory: historyInfo,
          dataVersion,
        };
      }
    },

    // 获取并处理历史记录（仅登录状态调用）
    fetchHistory: async ({ dataVersion, historyInfo }) => {
      try {
        // 在请求开始时，保存当前的 vuid（用于后续校验）
        const { vuid: startVuid } = get();
        set({ isLoading: true });
        // 传入 startVuid 用于在递归过程中检查 vuid 是否变化
        const { serverHistory, dataVersion: dataVersionNext } = await get().fetchAllHistory({
          historyInfo: [],
          startVuid,
          dataVersion,
          pageContext: '',
          hasNextPage: true,
        });
        // 合并线上历史记录和本地历史记录
        const mergeHistory = handleHistoryData(historyInfo, serverHistory);
        const { vuid: endVuid } = get();
        const isSanmeUser = getIsSameUser(startVuid, endVuid);
        // 如果不是同一个用户不更新状态
        if (!isSanmeUser) return;
        // 保存到 localStorage（登录状态）
        saveHistoryToStorage({ history: mergeHistory, dataVersion: dataVersionNext, vuid: endVuid });
        // 更新状态
        set({ curHistory: mergeHistory });
        // 根据流程图：对比本地记录与后台记录，如果本地记录时间戳领先，则分页上报（每30条）
        // 异步同步本地历史记录
        get().syncLocalHistoryIfNeeded(historyInfo, mergeHistory);
      } catch {
        set({ isLoading: false });
      }
    },

    // 获取历史记录 UI 数据
    fetchHistoryUiData: async (historyList) => {
      try {
        return await processBatchData(
          historyList,
          batchSize, // 每批处理30个
          async (batchRecords) => {
            const response = await queryHistoryUiData({
              records: batchRecords.map((item) => ({
                vid: item.vid,
                cid: item.cid,
                modifyTime: item.modifyTime,
                videoPlayTime: item.videoPlayTime,
                videoTotalTime: item.videoTotalTime,
              })),
            });

            if (!response?.code && !response.errCode && !response?.err_code) {
              return (response?.ui_data || [])?.map((item: { poster: Poster; record: HistoryRecord }) => ({
                ...item.poster,
                ...item.record,
              }));
            }
            return [];
          }
        );
      } catch {
        throw new Error('Failed to fetch history ui data');
      }
    },

    // 获取历史记录cid封面数据
    fetchHistoryCoverData: async (historyList) => {
      try {
        return await processBatchData(
          historyList,
          batchSize, // 每批处理30个
          async (batchRecords) => {
            const cids = batchRecords.map((item) => item.cid);
            const res = await getHistoryCidCover({ cids });
            return res.datas || [];
          }
        );
      } catch {
        throw new Error('Failed to fetch history cover data');
      }
    },

    // 查询单个历史记录信息
    // 传入 cid，查询 allHistory 中该 cid 下长短视频里的最新的一条 vid
    queryHistoryByCid: (cid) => {
      const { curHistory } = get();
      return queryHistoryByCid(curHistory, cid);
    },

    // 删除全部历史记录
    deleteAllHistory: async (): Promise<boolean> => {
      try {
        const { vuid } = get();
        const isLogin = !!vuid;
        let isSuccess = false;
        // 登录状态下删除全部历史记录信息
        if (isLogin) {
          const res = await deleteAllHistoryApi({ type: 0 }, {});
          isSuccess = res.err_code === 0;
        }
        const isUpdate = !isLogin || !!isSuccess;

        // 更新成功删除历史记录（内存和本地存储）
        if (isUpdate) deleteAllHistoryRecords(isLogin);
        // 清空内存中的状态
        set({
          curHistory: [],
          curShowHistory: [],
        });
        return isUpdate;
      } catch {
        return false;
      }
    },

    // 更新单条历史记录
    // operationType: 1-更新, 2-添加, 3-删除
    updateHistory: async (historyItem, operationType: OpType): Promise<boolean> => {
      const videotype = usePlayDetailStore.getState().getVidType(historyItem?.cid, historyItem?.vid);
      // 有vidType并且不等于Long，不更新，无vidType等于是首页直接 跳播放器上报
      // 目前直接跳播放器播放的是没有videotype信息的，但是全是长视频列表里的[有历史记录取历史记录，无历史记录为正片第一集]
      if (videotype && videotype !== VideoType.LONG && [OpType.OpTypeAdd, OpType.OpTypeUpdate].includes(operationType))
        return false;
      try {
        const { vuid, curHistory, curShowHistory } = get();
        // 判断是否登录
        const isLogin = !!vuid;
        let res = null;
        const curShowHistoryItem = null;
        let updatedShowHistory = [...curShowHistory];
        // 如果是登录状态，上报到后端
        if (isLogin) {
          // 接口请求参数
          const reportData = {
            dataVersion: historyItem.modifyTime || `${Date.now()}`,
            updated: [
              {
                record: {
                  vid: historyItem.vid,
                  cid: historyItem.cid,
                  modify_time: historyItem.modifyTime,
                  video_play_time: historyItem.videoPlayTime,
                  video_total_time: historyItem.videoTotalTime,
                },
                operation_type: operationType,
                record_type: IDType.RecordTypeCid,
              },
            ],
          };
          res = await reportHistory(reportData, {});
        }
        const isUpdate = !isLogin || !!res;
        if (isUpdate) {
          // 更新历史记录（内存和本地存储）
          // 删除操作需要计算最新的 allHistoryUiData 进行更新，展示页面
          // 播放过程中的历史记录上报由于缺少封面信息，需要下一次取历史记录信息时进行初始化使用uidata接口进行更新
          const updatedHistory = updateSingleHistory({
            currentHistory: curHistory,
            historyItem,
            operationType,
            vuid,
          });
          // 删除的只需要删除，更新操作[需要拉取海报信息]暂不处理，只更新currentHistory即可
          // 后续
          // 在进入首页以及历史记录页面以及播放页面时 需要手动再次拉取，调用initHistory方法就可以拿到currentHistory中的海报信息
          // 然后覆盖curShowHistory即可
          if (operationType === OpType.OpTypeDel) {
            updatedShowHistory = updateSingleHistoryForCurShow({
              currentHistory: curShowHistory,
              historyItem: (curShowHistoryItem || historyItem) as HistoryShowItem,
              operationType,
            });
          }
          set({ curHistory: updatedHistory, curShowHistory: updatedShowHistory });
        }
        // 返回删除结果，方便页面进行展示
        return isUpdate;
      } catch (error) {
        console.error('Failed to update history:', error);
        return false;
      }
    },

    // 同步当前后台时间
    syncServerTime: async () => {
      try {
        const res = await syncTime({}, {});
        if (res) {
          localStorage.setItem('player_report_gap', res.report_gap ?? '60');
          return res.time_stamp;
        }
        return null;
      } catch (error) {
        logger.error('syncServerTime error', error);
        return null;
      }
    },
  };
});
